import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { API_ENDPOINTS } from "./api/config";
import { useUser } from "./user_context";
import AuthLayout, { AuthAlert, AuthDivider } from "./components/AuthLayout";
import Button from "./components/ui/Button";
import Field from "./components/ui/Field";
import Input from "./components/ui/Input";
import PasswordInput from "./components/ui/PasswordInput";
import useSEO from "./hooks/useSEO";
import { setToken } from "./api/token";

export default function SignIn() {
  const navigate = useNavigate();
  useSEO({
    title: "Sign In",
    description: "Sign in to your RemboList account to find beauty, wellness, and hospitality jobs or manage your hiring.",
    path: "/signin",
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { userData, refreshUser } = useUser();

  // Already signed in (session resumed from the 30-day cookie): skip the form.
  useEffect(() => {
    if (userData) navigate(userData.role === "employer" ? "/employer/dashboard" : "/dashboard", { replace: true });
  }, [userData, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) { setError("Please fill in all fields."); return; }

    setLoading(true);

    try {
      const response = await fetch(API_ENDPOINTS.LOGIN, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Invalid credentials");
      }

      // Save token and role
      setToken(data.token);
      await refreshUser();
      // Redirect based on role
      if (data.user.role === "employer") {
        navigate("/employer/dashboard");
      } else {
        navigate("/dashboard");
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      footer={<>By signing in you agree to our <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>.</>}
    >
      <h1 className="rl-auth-title">Sign in</h1>
      <p className="rl-auth-subtitle">Access your dashboard, manage applications, and continue your journey.</p>

      {error && <AuthAlert>{error}</AuthAlert>}

      <form onSubmit={handleSubmit}>
        <Field label="Email address">
          <Input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            autoComplete="email"
          />
        </Field>
        <Field label="Password">
          <PasswordInput
            placeholder="Enter your password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoComplete="current-password"
          />
        </Field>
        <div className="rl-auth-link-row">
          <Link to="/forgot-password">Forgot password?</Link>
        </div>

        <Button type="submit" size="lg" fullWidth loading={loading}>
          {loading ? "Signing in…" : "Sign in"}
        </Button>
      </form>

      <AuthDivider />
      <Button as={Link} to="/signup" variant="secondary" size="lg" fullWidth>
        New to RemboList? Join now
      </Button>
    </AuthLayout>
  );
}
